import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaGlobe, FaVideo } from 'react-icons/fa';
import { API, BACKEND } from '../App';
import { useAuth } from '../context/AuthContext';
import { hasPremiumAccess } from '../config/subscriptions';
import TipTapEditor from '../components/TipTapEditor';
import { slugify } from '../utils/guides';
import { parseVideoUrl } from '../utils/videoUrl';

const CATEGORIES = [
  { id: 'maskiner',    name: 'Maskiner',                 icon: '🧊' },
  { id: 'produkter',   name: 'Produkter & Ingredienser', icon: '🍓' },
  { id: 'rengoring',   name: 'Rengøring & Vedligehold',  icon: '🧼' },
  { id: 'teknik',      name: 'Teknik & Udstyr',          icon: '⚙️' },
  { id: 'brugertips',  name: 'Brugertips & Erfaringer',  icon: '💡' },
  { id: 'tilbehor',    name: 'Tilbehør & Servering',     icon: '📦' },
];

const CreateTipPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Edit-mode: /tips/:slug/edit loader eksisterende guide. Create-mode:
  // /tips/create eller /guides/create (slug undefined → tomt form).
  const { slug: editSlug } = useParams();
  const isEditMode = Boolean(editSlug);
  const [editingId, setEditingId] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isEditMode);

  // Core fields
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [bodyRich, setBodyRich] = useState('');
  const [category, setCategory] = useState('maskiner');
  const [videoUrl, setVideoUrl] = useState('');
  const [isInternational, setIsInternational] = useState(false);

  // Slug — auto-suggested from title, admin-editable
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  // Status ('draft' | 'published')
  const [status, setStatus] = useState('published');

  // Image (coverbillede)
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageOwnershipConfirmed, setImageOwnershipConfirmed] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Load existing guide when in edit-mode.
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingExisting(true);
        const { data } = await axios.get(`${API}/tips/by-slug/${editSlug}`);
        if (cancelled) return;
        setEditingId(data.id);
        setTitle(data.title || '');
        setShortDescription(data.short_description || '');
        setBodyRich(data.body_rich || (data.content ? `<p>${_escapeHtml(data.content)}</p>` : ''));
        setCategory(data.category || 'maskiner');
        setVideoUrl(data.video_url || '');
        setIsInternational(!!data.is_international);
        setSlug(data.slug || slugify(data.title || ''));
        setSlugTouched(true); // Så title-ændring ikke auto-overskriver eksisterende slug.
        setStatus(data.status || (data.is_public ? 'published' : 'draft'));
        if (data.image_url) {
          const absUrl = data.image_url.startsWith('http') ? data.image_url : `${BACKEND}${data.image_url}`;
          setExistingImageUrl(absUrl);
          setImagePreview(absUrl);
        }
      } catch (err) {
        console.error('[EditGuide] fetch failed', err);
        toast.error('Kunne ikke indlæse guide til redigering.');
        navigate('/guides');
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, editSlug, navigate]);

  // Auto-slug when title changes (unless user has manually edited slug OR we're in edit-mode).
  useEffect(() => {
    if (!slugTouched && !isEditMode) setSlug(slugify(title));
  }, [title, slugTouched, isEditMode]);

  const videoInfo = useMemo(() => parseVideoUrl(videoUrl), [videoUrl]);
  const videoIsInvalid = videoUrl.trim() !== '' && !videoInfo;

  // Redirect if not authorized (guests / free users).
  if (!hasPremiumAccess(user)) {
    navigate('/guides');
    return null;
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Kun billedfiler er tilladt'); return; }
    if (file.size > 10 * 1024 * 1024)     { toast.error('Billede må maks. være 10MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageOwnershipConfirmed(false);
    setExistingImageUrl(null);
  };

  const handleSubmit = async (e, overrideStatus = null) => {
    e.preventDefault();

    const nextStatus = overrideStatus || status;

    // Validation
    if (!title.trim())                  { toast.error('Titel er påkrævet'); return; }
    if (title.length > 200)             { toast.error('Titel må maks. være 200 tegn'); return; }
    if (!bodyRich || bodyRich === '<p></p>') { toast.error('Indhold er påkrævet'); return; }
    if (shortDescription.length > 200)  { toast.error('Kort beskrivelse må maks. være 200 tegn'); return; }
    if (videoIsInvalid)                 { toast.error('Video-URL genkendes ikke (understøttet: YouTube, YouTube Shorts, Vimeo)'); return; }
    if (imageFile && !imageOwnershipConfirmed) { toast.error('Du skal bekræfte at du ejer billedet'); return; }

    try {
      setSubmitting(true);
      const plainExcerpt = _htmlToPlain(bodyRich).slice(0, 990);
      const payload = {
        title: title.trim(),
        content: plainExcerpt,
        body_rich: bodyRich,
        category,
        short_description: shortDescription.trim() || undefined,
        video_url: videoUrl.trim() || undefined,
        slug: slug || undefined,
        status: nextStatus,
        is_international: isInternational,
      };

      let tipId;
      let finalSlug;
      if (isEditMode && editingId) {
        // PUT — opdater eksisterende guide (kun tilladte felter).
        const putBody = {
          title: payload.title,
          content: payload.content,
          category: payload.category,
          slug: payload.slug,
          short_description: payload.short_description,
          body_rich: payload.body_rich,
          status: payload.status,
          video_url: payload.video_url,
        };
        // Cover-fjernelse: hvis brugeren har fjernet eksisterende cover
        // (og ikke uploadet en ny), send `image_url: null` så backend
        // clearer feltet. Ellers rører vi ikke feltet (sentinel-mønster).
        if (!imageFile && !imagePreview && existingImageUrl) {
          putBody.image_url = null;
        }
        const { data } = await axios.put(`${API}/tips/${editingId}`, putBody);
        tipId = editingId;
        finalSlug = data?.slug || slug;
      } else {
        const response = await axios.post(`${API}/tips`, payload);
        tipId = response.data.id;
        finalSlug = response.data.slug || slug;
      }

      // Upload nyt coverbillede (kun hvis brugeren har valgt en ny fil).
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        try {
          await axios.post(`${API}/tips/${tipId}/upload-cover`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (imgError) {
          console.error('Error uploading cover:', imgError);
          toast.error(isEditMode
            ? 'Guide opdateret, men coverbillede kunne ikke uploades'
            : 'Guide oprettet, men coverbillede kunne ikke uploades');
        }
      }

      const successMsg = isEditMode
        ? (nextStatus === 'draft' ? '📝 Gemt som kladde' : '✅ Guide opdateret!')
        : (nextStatus === 'draft' ? '📝 Gemt som kladde — kan udgives fra admin' : '✅ Din guide er nu udgivet!');
      toast.success(successMsg);

      if (nextStatus === 'published' && finalSlug) {
        navigate(`/guides/${finalSlug}`);
      } else {
        navigate('/guides');
      }
    } catch (error) {
      console.error('Error saving guide:', error);
      const detail = error.response?.data?.detail || 'Kunne ikke gemme guide. Prøv igen.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-gray-500" data-testid="edit-guide-loading">
        Indlæser guide til redigering…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid={isEditMode ? 'edit-guide-page' : 'create-guide-page'}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate(isEditMode && slug ? `/guides/${slug}` : '/guides')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          data-testid="create-guide-back"
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold">{isEditMode ? 'Rediger guide' : 'Skriv en guide'}</h1>
          <p className="text-gray-600">
            {isEditMode
              ? 'Opdater indholdet — ændringer gemmes ved klik på "Gem ændringer".'
              : 'Del din viden — bliv en del af SlushBook Tips & Guides'}
          </p>
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, null)} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-6">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Kategori *</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                data-testid={`create-guide-cat-${cat.id}`}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  category === cat.id
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-cyan-300'
                }`}
              >
                <span className="text-xl">{cat.icon}</span>
                <span className="text-sm font-medium">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titel * <span className="text-gray-400 text-xs">({title.length}/200 tegn)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="F.eks. 'Sådan justerer du temperaturen på Elmeco maskiner'"
            maxLength={200}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            required
            data-testid="create-guide-title"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL-slug <span className="text-gray-400 text-xs">(auto-genereret — kan redigeres)</span>
          </label>
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
            <span className="text-gray-500 text-sm select-none">/guides/</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
              placeholder="hvordan-fryser-min-slush-ikke"
              className="flex-1 bg-transparent focus:outline-none text-sm"
              data-testid="create-guide-slug"
            />
          </div>
        </div>

        {/* Short description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kort beskrivelse <span className="text-gray-400 text-xs">({shortDescription.length}/200 tegn — bruges til oversigter, Google og Facebook)</span>
          </label>
          <textarea
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            placeholder="En kort intro der får læseren til at klikke. Vises i søgeresultater og delinger."
            maxLength={200}
            rows={2}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
            data-testid="create-guide-short-description"
          />
        </div>

        {/* Rich Text Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Indhold *</label>
          <TipTapEditor
            value={bodyRich}
            onChange={setBodyRich}
            uploadToTipId={editingId}
            placeholder="Skriv din guide her. Brug overskrifter, punktlister og billeder for at gøre den let at læse."
          />
          <p className="text-xs text-gray-500 mt-2">
            💡 Tip: Brug H2 og H3 til at strukturere din guide. Indsæt billeder direkte via Ctrl+V eller billede-knappen.
          </p>
        </div>

        {/* Video URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FaVideo className="inline mr-1 text-gray-500" />
            Video (valgfrit) <span className="text-gray-400 text-xs">— YouTube, YouTube Shorts eller Vimeo</span>
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=… eller https://vimeo.com/…"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
              videoIsInvalid ? 'border-red-400 focus:ring-red-400' : 'border-gray-200 focus:ring-cyan-500'
            }`}
            data-testid="create-guide-video-url"
          />
          {videoIsInvalid && (
            <p className="text-xs text-red-600 mt-1">URL genkendes ikke. Understøttet: YouTube, YouTube Shorts, Vimeo.</p>
          )}
          {videoInfo && (
            <p className="text-xs text-green-700 mt-1">✓ {videoInfo.provider === 'youtube' ? 'YouTube' : 'Vimeo'}-video vil blive indlejret på guidesiden.</p>
          )}
        </div>

        {/* Coverbillede */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coverbillede (valgfrit) <span className="text-gray-400 text-xs">— vises øverst på guiden og ved deling på Facebook</span>
          </label>

          {!imagePreview ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-400 transition-colors">
              <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="cover-upload" data-testid="create-guide-cover-input" />
              <label htmlFor="cover-upload" className="cursor-pointer flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <span className="text-cyan-600 font-medium">Klik for at uploade</span>
                  <span className="text-gray-500 text-sm"> eller træk og slip</span>
                </div>
                <p className="text-xs text-gray-500">Billede komprimeres automatisk til max 80KB</p>
              </label>
            </div>
          ) : (
            <div className="relative">
              <img src={imagePreview} alt="Coverbillede preview" className="w-full h-48 object-cover rounded-lg" />
              <button type="button" onClick={removeImage} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {imageFile && (
            <div className="mt-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={imageOwnershipConfirmed}
                       onChange={(e) => setImageOwnershipConfirmed(e.target.checked)}
                       className="mt-1 w-5 h-5 text-cyan-500 rounded" required
                       data-testid="create-guide-cover-confirm" />
                <span className="text-sm text-gray-700">Jeg bekræfter, at jeg ejer billedet, og at det ikke er beskyttet af copyright.</span>
              </label>
            </div>
          )}
        </div>

        {/* International */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={isInternational}
                   onChange={(e) => setIsInternational(e.target.checked)}
                   className="w-5 h-5 text-cyan-500 rounded" />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-gray-900">
                <FaGlobe className="text-blue-500" />
                <span>International guide</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Markér, hvis din guide er relevant for brugere i alle lande.</p>
            </div>
          </label>
        </div>

        {/* Status radio */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" value="published" checked={status === 'published'}
                     onChange={() => setStatus('published')} data-testid="create-guide-status-published" />
              <span className="text-sm"><strong>Udgivet</strong> — synlig for alle med det samme</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" value="draft" checked={status === 'draft'}
                     onChange={() => setStatus('draft')} data-testid="create-guide-status-draft" />
              <span className="text-sm"><strong>Kladde</strong> — kun synlig for admins</span>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => navigate('/guides')}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Annuller
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, 'draft')}
            disabled={submitting || !title.trim()}
            className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            data-testid="create-guide-save-draft"
          >
            {submitting ? 'Gemmer…' : 'Gem som kladde'}
          </button>
          <button
            type="submit"
            disabled={submitting || !title.trim()}
            className="flex-1 px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 font-medium"
            data-testid="create-guide-submit"
          >
            {submitting
              ? (isEditMode ? 'Gemmer…' : 'Udgiver…')
              : isEditMode
                ? (status === 'draft' ? 'Gem som kladde' : 'Gem ændringer')
                : (status === 'draft' ? 'Gem som kladde' : 'Udgiv guide')}
          </button>
        </div>
      </form>
    </div>
  );
};

// Fast plain-text extraction for the legacy `content` field.
function _htmlToPlain(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
}

function _escapeHtml(s) {
  const div = document.createElement('div');
  div.textContent = s || '';
  return div.innerHTML;
}

export default CreateTipPage;
